import React from 'react';
import _ from 'lodash';
import moment from 'moment';
import { HyperLink } from 'components/base/HyperLink';
import * as LighthouseService from 'common/services/lighthouse.service';
import Icon from 'components/base/Icon';
import { Flex } from 'components/base/layout-components';
import { LINE_HEIGHT } from 'common/constants/sizes';
import Text from 'components/base/Text';
import * as TrackingService from 'common/services/tracking.service';
import { TRACKING } from 'common/constants/constants';
import { notificationTypeConfig } from './notification.config';

export const notificationToUiMapper =
  (lastViewTime, history) => notification => {
    const {
      actions,
      created_at,
      metadata = {},
      severity,
      notification_dedup_key
    } = notification;
    const {
      issue_title,
      issue_provider_key,
      issue_html_url,
      issuer_name,
      repository_name,
      pull_request_title,
      notification_id: notificationType,
      deep_linking,
      notification_version
    } = metadata;
    const thresholds = _.get(metadata, 'thresholds', {});

    const {
      title = _.noop,
      goal,
      headsUp = _.noop,
      thresholdValues,
      showOwner = false,
      pathFormat
    } = notificationTypeConfig[notificationType];

    const isV2 = notification_version === 'v2.0';
    const thresholdsData = isV2 ? thresholds : metadata;
    const thresholdsValuesByKey = thresholdValues
      ? thresholdValues({ thresholdsData, isV2 })
      : {};

    const isNew =
      !lastViewTime || moment(created_at).isSameOrAfter(lastViewTime);
    return {
      isNew,
      notificationId: notification_dedup_key,
      title: severity === 'warning' ? headsUp(metadata) : title(metadata),
      notificationAt: moment(created_at).format('YYYY-MM-DD HH:mm:ss'),
      content: (
        <Flex direction='column' width='100%'>
          {goal && (
            <Text
              size='medium'
              color='shuttleGray'
              lineHeight={LINE_HEIGHT.medium}
            >
              <b>Goal: </b>
              {goal(thresholdsValuesByKey)}
            </Text>
          )}
          <Flex pb='x-small' pt='small'>
            <Text size='small' pr='0.2rem'>
              PR:
            </Text>
            <HyperLink
              size='small'
              target='_blank'
              href={deep_linking}
              onClick={() =>
                TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
                  page: TRACKING.PAGES.DASHBOARD,
                  page_tab: TRACKING.PAGE_TABS.NOTIFICATIONS,
                  click_type: TRACKING.CLICK.GIT_REDIRECT
                })
              }
            >
              {repository_name}/{pull_request_title}
            </HyperLink>
          </Flex>
          {showOwner && (
            <Flex pb='x-small' pt='small' items='center'>
              <Icon name='user' size='smaller' color='chambray' />
              <Text size='small' pl='0.5rem' pr='0.2rem'>
                PR Owner: {issuer_name}
              </Text>
            </Flex>
          )}
          {issue_title && (
            <Flex pb='x-small' pt='small'>
              <Text size='small' pr='0.2rem' whiteSpace='nowrap'>
                Related Issue:
              </Text>
              <HyperLink
                size='small'
                target='_blank'
                href={issue_html_url}
                onClick={() =>
                  TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
                    page: TRACKING.PAGES.DASHBOARD,
                    page_tab: TRACKING.PAGE_TABS.NOTIFICATIONS,
                    click_type: TRACKING.CLICK.JIRA_REDIRECT
                  })
                }
              >
                {issue_provider_key} - {issue_title}
              </HyperLink>
            </Flex>
          )}
        </Flex>
      ),
      buttons: actions.map(action => {
        const { text = '', type } = action;
        return {
          text,
          buttonProps: {
            ...(type === 'lightHouse'
              ? {
                  style: {
                    minWidth: '30px'
                  }
                }
              : {}),
            onClick: getButtonOnClick(
              action,
              history,
              pathFormat,
              thresholdsValuesByKey
            )
          }
        };
      })
    };
  };

const getButtonOnClick = (
  action,
  history,
  pathFormat,
  thresholdsValuesByKey
) => {
  const { type, articleId, path } = action;
  const pathFormatted =
    pathFormat && path ? pathFormat(path, thresholdsValuesByKey) : path;
  switch (type) {
    case 'lightHouse':
      return () => {
        TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
          page: TRACKING.PAGES.DASHBOARD,
          page_tab: TRACKING.PAGE_TABS.NOTIFICATIONS,
          click_type: TRACKING.CLICK.HELP_DOCS
        });
        LighthouseService.navigateArticle(articleId);
      };
    case 'appLink':
      return () => {
        TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
          page: TRACKING.PAGES.DASHBOARD,
          page_tab: TRACKING.PAGE_TABS.NOTIFICATIONS,
          click_type: TRACKING.CLICK.SEE_MORE
        });

        history.push(pathFormatted);
      };
    default:
      return _.noop;
  }
};
